import React, { useEffect } from 'react';

import { getErrorMessage } from '~libs/axiosWrapper';
import { getMonthDateRange } from '~libs/dayjs';

import { AlertList, ErrorResponse, Spinner } from '~components';

import { errorColumns, warningColumns } from '~support-at-home/forms/statements/estimateLists';

import { useEstimateQuery } from '~support-at-home/actions/statements';

import { DateRange, ErrorDetailsCard, SummaryStatCard, SummaryStatCluster, WarningDetailsCard } from '../common';

const STATEMENT_WARNINGS = [
  'Ensure all claims within statement period are finalised and reconciled before generating.',
  'Only one statement per client can be generated for the statement period.',
  'Claims reconciled / finalised after statement generation will be carried over to the next statement.',
];

interface ComponentProps {
  value: { client_id: number[]; month: string; include_empty?: boolean };
  onChange?: (name: string, value: any) => void;
}

const Confirm: React.FC<ComponentProps> = ({ value, onChange }) => {
  const { client_id = [], month, include_empty } = value;
  const { startDate, endDate } = getMonthDateRange(month);

  const { data, error, isLoading } = useEstimateQuery({
    client_ids: client_id,
    start_date: startDate,
    end_date: endDate,
    include_empty,
  });

  // Expose data to parent form for validation
  useEffect(() => {
    if (onChange && data) {
      onChange('confirm', { data });
    }
  }, [data]);

  if (isLoading) return <Spinner loading={isLoading} />;

  const { statements, items, warnings = [] } = data || {};
  const { to_generate = 0, existing = 0, invalid: invalidStatements = 0 } = statements || {};
  const { in_range = 0, carried_over = 0, invalid: invalidItems = 0 } = items || {};
  const warningCount = warnings.length;

  return (
    <div>
      <DateRange start_date={startDate} end_date={endDate} title="Statement Period" />
      <AlertList header="Caution" color="warning" listItems={STATEMENT_WARNINGS} />
      {error && <ErrorResponse message={getErrorMessage(error)} />}
      {data && warningCount > 0 && (
        <WarningDetailsCard
          id="statements-warning-details"
          title="Statement Warnings"
          warnings={warnings}
          columns={warningColumns}
        />
      )}
      {data && (data?.errors?.length ?? 0) > 0 && (
        <ErrorDetailsCard
          id="statements-error-details"
          title="Invalid Item Details"
          errors={data.errors}
          columns={errorColumns}
        />
      )}
      {data && (
        <div className="d-flex flex-wrap mt-4 gap-4">
          {/* Statements */}
          <SummaryStatCluster title="Statements">
            <SummaryStatCard count={to_generate} countLabel="To Generate" countClassName="text-success" />
            <SummaryStatCard count={existing} countLabel="Existing" />
            <SummaryStatCard count={invalidStatements} countLabel="Ignored" countClassName="text-danger" />
            <SummaryStatCard count={warningCount} countLabel="Warnings" countClassName="text-warning" />
          </SummaryStatCluster>

          {/* Claim Items */}
          <SummaryStatCluster title="Items">
            <SummaryStatCard count={in_range} countLabel="In Range" countClassName="text-success" />
            <SummaryStatCard count={carried_over} countLabel="Carried Over" />
            <SummaryStatCard count={invalidItems} countLabel="Invalid" countClassName="text-danger" />
          </SummaryStatCluster>
        </div>
      )}
    </div>
  );
};

export default Confirm;
